<template>
  <div class="mx-auto">
    <chat-notification :title="notificationTitle">
      <div class="flex max-w-full flex-wrap items-center justify-center gap-x-1 text-center">
        <template v-if="isOwnPin">
          <span class="text-center">
            {{ messageText }}
            <span
              v-if="pinnedMessageText"
              data-test="chat-pinned-event-quote"
            >«{{ pinnedMessageText }}»</span>
          </span>
        </template>
        <template v-else>
          <span
            v-if="executorDisplayName"
            data-test="chat-pinned-event-executor"
          >
            {{ executorDisplayName }}
          </span>
          <span class="text-center">
            {{ messageText }}
            <span
              v-if="pinnedMessageText"
              data-test="chat-pinned-event-quote"
            >«{{ pinnedMessageText }}»</span>
          </span>
        </template>
      </div>
    </chat-notification>
    <div
      v-if="isUnread"
      class="relative"
    >
      <span class="absolute right-[-10px] bottom-0 h-2 w-2 rounded-full bg-primary-tints-400" />
    </div>
  </div>
</template>
<script setup lang="ts">
import { HistoryMessageItem, MessageIncomingEvent, MessageOutgoingEvent, MessagePinnedByOtherEvent, MessagePinnedByYouEvent, MessageUnPinnedByOtherEvent, MessageUnPinnedByYouEvent } from '@wui/im'
import { computed } from 'vue'

import { useStoreContacts } from '@/entities/contact'
import {
  chatModel,
  getPinnedEventText,
  getPinnedSystemQuote,
  truncatePinnedSystemPreview,
  usePinEventsStore,
  useSafeUnreadState,
} from '@/entities/messages'
import { useStoreRooms } from '@/entities/room'

import { ChatNotification } from '@/shared/components/chat-notification'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'

const props = defineProps<{
  event: HistoryMessageItem<MessagePinnedByOtherEvent | MessagePinnedByYouEvent | MessageUnPinnedByOtherEvent | MessageUnPinnedByYouEvent>
}>()

const { findById } = chatModel.useChatMessageStore()
const { getPinEventsForRoom } = usePinEventsStore()

const pinnedMessageText = computed(() => {
  const targetEventId = props.event.item.targetEventId
  const historyMessage = findById<MessageOutgoingEvent | MessageIncomingEvent>(targetEventId)
  // После reload target может быть вне viewport — берём данные из pin store
  const pin = getPinEventsForRoom(props.event.item.pServed)?.find((entry) => entry.eventId === targetEventId)
  return truncatePinnedSystemPreview(getPinnedSystemQuote(pin, historyMessage).text)
})

const { currentUser } = useCurrentUser()
const { t } = useLocalization()
const { findGuestByPserved } = useStoreRooms()

const isOwnPin = computed(() => currentUser.value?.imLogin === props.event.item.executor)

const messageText = computed(() => getPinnedEventText(props.event, {
  isOwn: isOwnPin.value,
  hasPreviewText: Boolean(pinnedMessageText.value),
}))

const contact = computed(() => {
  if (isOwnPin.value) {
    return currentUser.value
  }
  return useStoreContacts().findByPServed(props.event.item.executor)
})

const isGuestExecutor = computed(() => Boolean(props.event.item.executor?.includes('<sip:GUEST')))

const guestDisplayName = computed(() => {
  if (!isGuestExecutor.value) {
    return undefined
  }
  const name = findGuestByPserved(props.event.item.pServed, props.event.item.executor)
  return name ? `${name} (${t('common.guest')})` : undefined
})

const executorDisplayName = computed(() =>
  contact.value?.name
    ?? guestDisplayName.value
    ?? (isGuestExecutor.value ? t('common.guest') : ''),
)

const notificationTitle = computed(() => {
  const quotePart = pinnedMessageText.value ? ` «${pinnedMessageText.value}»` : ''
  const rest = `${messageText.value}${quotePart}`
  if (isOwnPin.value) {
    return rest
  }
  return executorDisplayName.value ? `${executorDisplayName.value} ${rest}` : rest
})

const { isUnread } = useSafeUnreadState(props.event)
</script>
